import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { VaultNode } from '@/lib/api'

/**
 * Navigation state for the /vault three-pane workspace. Selected node + expand
 * set + view toggles, shared across the tree (left), page (center) and search
 * bar. Persisted so reopening restores where you were.
 */
interface VaultState {
  selectedNodeId: string | null
  /** A node the tree should reveal (expand its ancestor path) + scroll to. Set
   *  by any surface that selects a node out-of-tree (search hit, deep-link,
   *  Concierge). The tree consumes it then clears it. Not persisted. */
  revealTarget: VaultNode | null
  expandedIds: string[]
  showArchived: boolean
  query: string
  conciergeOpen: boolean
  treeOpen: boolean
  treeWidth: number
  conciergeWidth: number
  /** True while a card/note is being actively edited — pauses background
   *  auto-refresh so a refetch can't disturb in-flight typing. Not persisted. */
  editing: boolean
  /** Active pane on the narrow/mobile single-pane layout. Lets components
   *  outside VaultPage (e.g. the editor toolbar) know which tab is showing so
   *  a portaled/fixed element doesn't bleed onto the other tabs. Not persisted. */
  pane: 'tree' | 'page' | 'concierge'

  select: (id: string | null) => void
  /** Select a node AND ask the tree to reveal/scroll to it. Pass null to clear. */
  requestReveal: (node: VaultNode | null) => void
  toggleExpanded: (id: string) => void
  expand: (id: string) => void
  /** Replace the whole expansion set in one shot (reveal-active + collapse-rest). */
  setExpanded: (ids: string[]) => void
  isExpanded: (id: string) => boolean
  setShowArchived: (v: boolean) => void
  setQuery: (q: string) => void
  setConciergeOpen: (v: boolean) => void
  setTreeOpen: (v: boolean) => void
  setTreeWidth: (w: number) => void
  setConciergeWidth: (w: number) => void
  setEditing: (v: boolean) => void
  setPane: (p: 'tree' | 'page' | 'concierge') => void
}

export const useVaultStore = create<VaultState>()(
  persist(
    (set, get) => ({
      selectedNodeId: null,
      revealTarget: null,
      expandedIds: [],
      showArchived: false,
      query: '',
      conciergeOpen: true,
      treeOpen: true,
      treeWidth: 288,
      conciergeWidth: 380,
      editing: false,
      pane: 'tree',

      select: (id) => set({ selectedNodeId: id }),
      requestReveal: (node) =>
        set(node ? { revealTarget: node, selectedNodeId: node.id } : { revealTarget: null }),
      toggleExpanded: (id) =>
        set((s) => ({
          expandedIds: s.expandedIds.includes(id)
            ? s.expandedIds.filter((x) => x !== id)
            : [...s.expandedIds, id],
        })),
      expand: (id) =>
        set((s) => (s.expandedIds.includes(id) ? s : { expandedIds: [...s.expandedIds, id] })),
      setExpanded: (ids) => set({ expandedIds: Array.from(new Set(ids)) }),
      isExpanded: (id) => get().expandedIds.includes(id),
      setShowArchived: (v) => set({ showArchived: v }),
      setQuery: (q) => set({ query: q }),
      setConciergeOpen: (v) => set({ conciergeOpen: v }),
      setTreeOpen: (v) => set({ treeOpen: v }),
      setTreeWidth: (w) => set({ treeWidth: Math.max(200, Math.min(520, Math.round(w))) }),
      setConciergeWidth: (w) => set({ conciergeWidth: Math.max(280, Math.min(640, Math.round(w))) }),
      setEditing: (v) => set({ editing: v }),
      setPane: (p) => set({ pane: p }),
    }),
    {
      name: 'vault-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        selectedNodeId: s.selectedNodeId,
        expandedIds: s.expandedIds,
        showArchived: s.showArchived,
        query: s.query,
        conciergeOpen: s.conciergeOpen,
        treeOpen: s.treeOpen,
        treeWidth: s.treeWidth,
        conciergeWidth: s.conciergeWidth,
      }),
    }
  )
)
